import React, { useState, useEffect } from 'react';
import { X, Copy, Check } from 'lucide-react';
import { formsApi } from '../../../services/api';
import { showError } from '../../../utils/toast';

interface ShareKeyModalProps {
  isOpen: boolean;
  onClose: () => void;
  formId: number;
}

interface ShareKeyData {
  shareable_link: string;
  key: string;
  form_id: number;
  handler: string;
  title: string;
}

const ShareKeyModal: React.FC<ShareKeyModalProps> = ({ isOpen, onClose, formId }) => {
  const [data, setData] = useState<ShareKeyData | null>(null);
  const [loading, setLoading] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  const [copiedKey, setCopiedKey] = useState(false);

  useEffect(() => {
    if (isOpen && formId) {
      fetchShareKey();
    } else {
      setData(null);
      setCopiedLink(false);
      setCopiedKey(false);
    }
  }, [isOpen, formId]);

  const fetchShareKey = async () => {
    try {
      setLoading(true);
      const res = await formsApi.generateShareKey(formId);
      setData(res);
    } catch (error: any) {
      showError(error.response?.data?.message || 'Failed to generate share key');
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text: string, type: 'link' | 'key') => {
    navigator.clipboard.writeText(text);
    if (type === 'link') {
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2000);
    } else {
      setCopiedKey(true);
      setTimeout(() => setCopiedKey(false), 2000);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center pt-16 sm:pt-0 pb-16 sm:pb-0 px-4">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-lg rounded-2xl shadow-xl overflow-hidden animate-fade-in theme-card theme-border border">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl font-semibold theme-text-primary">Share Form</h2>
              <p className="text-sm theme-text-secondary">Generate and copy your share key</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 theme-text-secondary hover:theme-bg-secondary rounded-full transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          {loading ? (
            <div className="py-8 flex justify-center">
              <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : data ? (
            <div className="space-y-4">
              {/* Shareable Link */}
              <div className="space-y-1">
                <label className="text-sm font-medium theme-text-secondary">
                  Shareable Link
                </label>
                <div className="flex group">
                  <input
                    type="text"
                    readOnly
                    value={`${window.location.origin}/f/${data.handler}?key=${data.key}`}
                    className="flex-1 min-w-0 p-3 theme-bg-primary theme-border border-y border-l border-r-0 rounded-l-lg theme-text-primary focus:outline-none"
                  />
                  <button
                    onClick={() => copyToClipboard(`${window.location.origin}/f/${data.handler}?key=${data.key}`, 'link')}
                    className="px-4 bg-blue-600 text-white rounded-r-lg hover:bg-blue-700 transition-colors flex items-center gap-2 font-medium"
                  >
                    {copiedLink ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    {copiedLink ? 'Copied' : 'Copy'}
                  </button>
                </div>
              </div>

              {/* Share Key */}
              <div className="space-y-1">
                <label className="text-sm font-medium theme-text-secondary">
                  Share Key
                </label>
                <div className="flex group">
                  <input
                    type="text"
                    readOnly
                    value={data.key}
                    className="flex-1 min-w-0 p-3 theme-bg-primary theme-border border-y border-l border-r-0 rounded-l-lg theme-text-primary focus:outline-none font-mono text-sm"
                  />
                  <button
                    onClick={() => copyToClipboard(data.key, 'key')}
                    className="px-4 bg-gray-600 text-white rounded-r-lg hover:bg-gray-700 transition-colors flex items-center gap-2 font-medium"
                  >
                    {copiedKey ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    {copiedKey ? 'Copied' : 'Copy'}
                  </button>
                </div>
                <p className="text-xs theme-text-tertiary mt-1">
                  Users can use this key to access the form if they don't have the direct link.
                </p>
              </div>

              {/* Form Details Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
                {/* Title */}
                <div className="space-y-1">
                  <label className="text-sm font-medium theme-text-secondary">
                    Form Title
                  </label>
                  <input
                    type="text"
                    readOnly
                    value={data.title}
                    className="w-full p-2.5 theme-bg-primary theme-border border rounded-lg theme-text-primary focus:outline-none"
                  />
                </div>

                {/* Handler */}
                <div className="space-y-1">
                  <label className="text-sm font-medium theme-text-secondary">
                    Handler
                  </label>
                  <input
                    type="text"
                    readOnly
                    value={data.handler}
                    className="w-full p-2.5 theme-bg-primary theme-border border rounded-lg theme-text-primary focus:outline-none"
                  />
                </div>

                {/* Form ID */}
                <div className="space-y-1 sm:col-span-2">
                  <label className="text-sm font-medium theme-text-secondary">
                    Form ID
                  </label>
                  <input
                    type="text"
                    readOnly
                    value={data.form_id.toString()}
                    className="w-full p-2.5 theme-bg-primary theme-border border rounded-lg theme-text-primary focus:outline-none font-mono text-sm"
                  />
                </div>
              </div>
            </div>
          ) : (
             <div className="py-8 text-center text-red-500">
               Failed to load share data.
             </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ShareKeyModal;
